import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";

/**
 * Tracks per-tab "has unsaved changes" flags inside the Settings shell.
 *
 * - `useDirty(tabId, isDirty)` — embedded forms call this whenever their
 *   state diverges from / converges back to the saved snapshot.
 * - The shell auto-attaches `beforeunload` for browser-level discards
 *   (refresh / close / external nav) when ANY tab is dirty.
 * - The shell also captures internal SPA link clicks at the document level —
 *   if the user is navigating away from `/settings` and anything is dirty,
 *   we show a confirm dialog. Clicks within `/settings` (e.g., switching
 *   tabs) are unaffected because we keep tabs mounted.
 */
const SettingsDirtyContext = createContext(null);

export function SettingsDirtyProvider({ children }) {
    const [dirtyTabs, setDirtyTabs] = useState(() => new Set());

    const setTabDirty = useCallback((tabId, isDirty) => {
        setDirtyTabs((prev) => {
            const has = prev.has(tabId);
            if (isDirty && has) return prev;
            if (!isDirty && !has) return prev;
            const next = new Set(prev);
            if (isDirty) next.add(tabId);
            else next.delete(tabId);
            return next;
        });
    }, []);

    // Tabs register their save() function so the shell can offer a "Save all"
    // batch action when multiple tabs are dirty at once.
    const saveHandlersRef = useRef({});
    const registerSaver = useCallback((tabId, saverFn) => {
        saveHandlersRef.current[tabId] = saverFn;
        return () => {
            if (saveHandlersRef.current[tabId] === saverFn) {
                delete saveHandlersRef.current[tabId];
            }
        };
    }, []);
    const saveAll = useCallback(async (tabIds) => {
        const ids = tabIds || Array.from(dirtyTabs);
        const results = [];
        for (const id of ids) {
            const fn = saveHandlersRef.current[id];
            if (!fn) continue;
            try {
                await fn();
                results.push({ id, ok: true });
            } catch (err) {
                results.push({ id, ok: false, err });
            }
        }
        return results;
    }, [dirtyTabs]);

    const anyDirty = dirtyTabs.size > 0;
    const dirtyRef = useRef(anyDirty);
    dirtyRef.current = anyDirty;

    // Browser-level guard — refresh, close tab, type a new URL.
    useEffect(() => {
        if (!anyDirty) return undefined;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = ""; // required for Chrome
            return "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [anyDirty]);

    // SPA navigation guard — capture clicks on any `<a href>` that would
    // leave `/settings`. If dirty, show a confirm.
    useEffect(() => {
        const onClick = (e) => {
            if (!dirtyRef.current) return;
            const link = e.target.closest && e.target.closest("a[href]");
            if (!link) return;
            // Ignore new-tab / modifier clicks — the current tab won't change.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            if (link.target && link.target !== "" && link.target !== "_self") return;
            const href = link.getAttribute("href") || "";
            // External or hash-only links — let beforeunload handle externals.
            if (!href.startsWith("/")) return;
            // Stay-within-settings navigations (e.g., ?tab= changes) are fine.
            if (href === "/settings" || href.startsWith("/settings?") || href.startsWith("/settings#")) return;
            const ok = window.confirm(
                "You have unsaved changes. Discard them and leave Settings?"
            );
            if (!ok) {
                e.preventDefault();
                e.stopPropagation();
            } else {
                // User accepted — clear flags so we don't double-prompt on the
                // next click (e.g., the destination page refires).
                setDirtyTabs(new Set());
            }
        };
        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, []);

    const value = useMemo(
        () => ({ dirtyTabs, anyDirty, setTabDirty, registerSaver, saveAll }),
        [dirtyTabs, anyDirty, setTabDirty, registerSaver, saveAll],
    );

    return (
        <SettingsDirtyContext.Provider value={value}>{children}</SettingsDirtyContext.Provider>
    );
}

/**
 * Embedded form hook — pass the tabId, dirty bool, and (optionally) a save fn.
 * The save fn lets the shell trigger a batch save across multiple dirty tabs.
 */
export function useDirtyTab(tabId, isDirty, saveFn) {
    const ctx = useContext(SettingsDirtyContext);
    useEffect(() => {
        if (!ctx) return undefined;
        ctx.setTabDirty(tabId, !!isDirty);
        return () => ctx.setTabDirty(tabId, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabId, isDirty]);

    // Keep the saver registration in sync with the latest closure.
    useEffect(() => {
        if (!ctx || !saveFn) return undefined;
        return ctx.registerSaver(tabId, saveFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabId, saveFn]);
}

export function useSettingsDirty() {
    return useContext(SettingsDirtyContext);
}
